import {
  type AIAgent,
  getAgentId,
} from "@p0security/shared/types/ai-inventory";
import { useAuthFetch } from "components/Login/hook";
import { useGuardedEffect } from "hooks/useGuardedEffect";
import { useCallback, useMemo, useState } from "react";
import type { Cancellation } from "utils/cancel";

import type { McpAccessLogEvent } from "./useMcpAccessLogs.ts";

type EventsResp = { events: McpAccessLogEvent[] };

export type AgentActivity = {
  readonly lastSessionAt: number | undefined;
  readonly connectedServerIds: ReadonlyArray<string>;
  readonly loading: boolean;
};

const EMPTY_SERVER_IDS: ReadonlyArray<string> = [];

const localAgentClientId = (agent: AIAgent): string | undefined =>
  agent.platform === "local-agents" ? agent.data.id : undefined;

// Tightest signal of "this agent did real work" — protocol-level frames like
// `initialize` and `notifications/*` fire on every connection and would
// otherwise mark a client active just for opening a session.
const ACTIVITY_METHOD = "tools/call";
const PROBE_LIMIT = 200;

const isRealActivity = (e: McpAccessLogEvent): boolean => {
  const msg = e.event?.message;
  return msg?.kind === "request" && msg.method === ACTIVITY_METHOD;
};

type Probe = {
  readonly latest: number | undefined;
  readonly serverIds: ReadonlySet<string>;
};

const EMPTY_PROBE: Probe = { latest: undefined, serverIds: new Set() };

const fetchProbe = async (
  authFetch: ReturnType<typeof useAuthFetch>,
  params: Record<string, string>,
  cancellation: Cancellation
): Promise<Probe> => {
  const search = new URLSearchParams({ ...params, limit: String(PROBE_LIMIT) });
  const res = await authFetch(`mcp-access-log/events?${search.toString()}`, {
    method: "GET",
    cancellation,
  });
  if (!res || cancellation.isCancelled) return EMPTY_PROBE;
  const body = (await res.json()) as EventsResp;
  const real = (body.events ?? []).filter(isRealActivity);
  const serverIds = new Set<string>();
  for (const e of real) {
    const sid = e.event?.["mcp-server"]?.id;
    if (sid) serverIds.add(sid);
  }
  return { latest: real[0]?.event?.timestamp, serverIds };
};

export const EMPTY_ACTIVITY: AgentActivity = {
  lastSessionAt: undefined,
  connectedServerIds: EMPTY_SERVER_IDS,
  loading: false,
};

const probeAgent = async (
  authFetch: ReturnType<typeof useAuthFetch>,
  clientId: string | undefined,
  matchedServerIds: ReadonlyArray<string>,
  cancellation: Cancellation
): Promise<AgentActivity> => {
  if (!clientId && matchedServerIds.length === 0) return EMPTY_ACTIVITY;
  const probes: Promise<Probe>[] = [];
  if (clientId) probes.push(fetchProbe(authFetch, { clientId }, cancellation));
  for (const serverId of matchedServerIds) {
    probes.push(fetchProbe(authFetch, { serverId }, cancellation));
  }
  const results = await Promise.all(probes);
  let latest: number | undefined;
  const allServerIds = new Set<string>();
  for (const probe of results) {
    if (
      probe.latest !== undefined &&
      (latest === undefined || probe.latest > latest)
    ) {
      latest = probe.latest;
    }
    for (const sid of probe.serverIds) allServerIds.add(sid);
  }
  return {
    lastSessionAt: latest,
    connectedServerIds: [...allServerIds].sort(),
    loading: false,
  };
};

/**
 * Joins on both `azp` (local-agents client) and `mcp-server.id` (any matched
 * managed server) so Copilot Studio agents with active connectors light up
 * alongside Claude Code clients. Fires 1+N queries per agent — swap to a
 * batched activity-summary endpoint once the list outgrows a couple dozen
 * agents per tenant.
 */
export const useAgentActivities = (
  agents: ReadonlyArray<AIAgent>,
  matchedServerIdsByAgent: ReadonlyMap<string, ReadonlyArray<string>>
): ReadonlyMap<string, AgentActivity> => {
  const authFetch = useAuthFetch();
  const [byAgent, setByAgent] = useState<ReadonlyMap<string, AgentActivity>>(
    () => new Map()
  );

  const probeKey = useMemo(
    () =>
      agents
        .map((agent) => {
          const id = getAgentId(agent);
          const cid = localAgentClientId(agent) ?? "";
          const matched = [...(matchedServerIdsByAgent.get(id) ?? [])]
            .sort()
            .join(",");
          return `${id}|${cid}|${matched}`;
        })
        .join(";"),
    [agents, matchedServerIdsByAgent]
  );

  useGuardedEffect(
    (cancellation) => async () => {
      if (agents.length === 0) {
        setByAgent(new Map());
        return;
      }
      const entries = await Promise.all(
        agents.map(async (agent) => {
          const id = getAgentId(agent);
          const activity = await probeAgent(
            authFetch,
            localAgentClientId(agent),
            matchedServerIdsByAgent.get(id) ?? [],
            cancellation
          );
          return [id, activity] as const;
        })
      );
      if (cancellation.isCancelled) return;
      setByAgent(new Map(entries));
    },
    // probeKey is the canonical content-derived dep for agents +
    // matchedServerIdsByAgent; depending on those directly would refire on
    // every parent rebuild even when the underlying ids haven't changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authFetch, probeKey],
    useCallback(() => setByAgent(new Map()), [])
  );

  return byAgent;
};
